import { Plane, Car, ArrowRight, MapPin } from "lucide-react";
import { formatPrice } from "@/lib/currency";

/**
 * Phase 9 — Multi-modal route card.
 *
 * Surfaced when the user queries flights to a destination that has NO
 * commercial airport (Munnar, Ooty, Manali...). Lists 2-3 ranked
 * "fly to X then drive Y" options.
 *
 * The blue category dot reuses the existing Flights colour.
 */
export default function MultiModalRouteCard({ card }) {
    const { destination, options = [], currency = "INR" } = card;
    const fmt = (amt, ccy) => formatPrice({ amount: amt, currency: ccy || currency });

    return (
        <article
            className="relative rounded-2xl border border-[#E5E5E5] bg-white p-5"
            data-testid="multi-modal-route-card"
        >
            <span
                aria-hidden
                className="absolute right-4 top-4 inline-block h-2 w-2 rounded-full"
                style={{ background: "#2563EB" }}
            />
            <header className="mb-1 flex items-baseline gap-2">
                <MapPin size={14} strokeWidth={2} className="text-[#0A0A0A]" />
                <h3 className="text-[15px] font-semibold tracking-tight text-[#0A0A0A]">
                    How to reach {destination?.name}
                </h3>
            </header>
            <p className="mb-4 text-[12px] text-[#737373]">
                {destination?.name} has no airport — here are the best fly-then-drive routes.
            </p>

            <ol className="space-y-3">
                {options.map((o, i) => (
                    <li
                        key={o.via_airport?.iata + "-" + i}
                        className="rounded-xl border border-[#E5E5E5] p-4"
                        data-testid={`multi-modal-option-${o.via_airport?.iata}`}
                    >
                        <div className="mb-2 flex items-center justify-between">
                            <span className="inline-flex items-center gap-2 text-[10px] font-medium uppercase tracking-wider text-[#737373]">
                                <span className="rounded-full bg-[#0A0A0A] px-2 py-0.5 text-white">
                                    {o.label || `#${o.rank}`}
                                </span>
                                Via {o.via_airport?.city} ({o.via_airport?.iata})
                            </span>
                            <span className="text-[14px] font-semibold text-[#0A0A0A]">
                                {fmt(o.total_cost, o.currency)}
                            </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-[12.5px] text-[#0A0A0A]">
                            {o.flight ? (
                                <span className="inline-flex items-center gap-1.5">
                                    <Plane size={12} strokeWidth={2} className="text-[#2563EB]" />
                                    {fmt(o.flight.price, o.flight.currency)}
                                    {o.flight.carrier ? <em className="not-italic text-[#737373]">· {o.flight.carrier}</em> : null}
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1.5 text-[#737373]">
                                    <Plane size={12} strokeWidth={2} />
                                    No flight needed
                                </span>
                            )}
                            <ArrowRight size={11} className="text-[#A3A3A3]" />
                            <span className="inline-flex items-center gap-1.5">
                                <Car size={12} strokeWidth={2} className="text-[#0A0A0A]" />
                                {o.ground_transit?.duration_h}h cab · {o.ground_transit?.distance_km} km · {fmt(o.ground_transit?.cost, "INR")}
                            </span>
                        </div>
                        {o.flight?.book_url && (
                            <a
                                href={o.flight.book_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-3 inline-flex items-center justify-center rounded-lg bg-[#0A0A0A] px-3 py-1.5 text-[12px] font-medium text-white transition hover:bg-black"
                                data-testid={`multi-modal-book-${o.via_airport?.iata}`}
                            >
                                Book flight to {o.via_airport?.iata}
                            </a>
                        )}
                    </li>
                ))}
            </ol>
        </article>
    );
}
